import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { MainLayoutContainer } from "components/Layout";
import { TagManager } from "components/TagManager";
import { API } from "config/api";
import { API_ROUTES } from "config/apiRoutes";
import { AdminRoutes } from "config/routes";
import {
  useAddPostMutation,
  useAddPostTagsMutation,
  useGetOrganizationByUserIdQuery,
  useGetOrganizationDomainTagsByOrgIdsQuery,
  useGetOrganizationsQuery,
} from "graphql/__generated__/hooks";
import { useAppSelector } from "hooks/useAppSelector";
import { useAuthUser } from "hooks/useAuthUser";
import { VETCOIN_REWARDS, useVetcoin } from "hooks/useVetcoin";
import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";
import { PostAudienceRole, POST_AUDIENCE_ALL } from "types/post";
import { TAG_TYPE_DOMAIN, Tag } from "types/tag";
import { ROLES } from "types/user";

const AUDIENCE_OPTIONS: Array<{ value: PostAudienceRole; label: string }> = [
  { value: POST_AUDIENCE_ALL, label: "All" },
  { value: ROLES.COACH, label: "Coaches" },
  { value: ROLES.DIRECTOR, label: "Directors" },
  { value: ROLES.MANAGER, label: "Managers" },
  { value: ROLES.STUDENT, label: "Learners" },
];

// Default pseudo-domain shown when no domain is selected. It is not a real
// tag: posts without domain tags stay within the author's organization.
const MY_ORG_ONLY_DOMAIN: Tag = { name: "My Org only", type: TAG_TYPE_DOMAIN };

export const PostCreate: FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthUser();
  const organization = useAppSelector(organizationSelectedSelector);
  const orgId = organization?.org_id;
  const { awardVetcoins } = useVetcoin(user?.user_id);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audienceRole, setAudienceRole] =
    useState<PostAudienceRole>(POST_AUDIENCE_ALL);
  const [selectedDomains, setSelectedDomains] = useState<Tag[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = user?.role === ROLES.ADMIN;

  const { data: userOrgsData } = useGetOrganizationByUserIdQuery({
    variables: { userId: user?.user_id || "" },
    skip: !user?.user_id || isAdmin,
  });

  const { data: allOrgsData } = useGetOrganizationsQuery({
    skip: !isAdmin,
  });

  const myOrgIds = useMemo(() => {
    if (isAdmin) {
      return (allOrgsData?.organization || [])
        .map((org) => org.org_id)
        .filter((id): id is string => Boolean(id));
    }
    return (userOrgsData?.organization || [])
      .map((org) => org.org_id)
      .filter((id): id is string => Boolean(id));
  }, [isAdmin, allOrgsData, userOrgsData]);

  const { data: domainData } = useGetOrganizationDomainTagsByOrgIdsQuery({
    variables: { orgIds: myOrgIds },
    skip: myOrgIds.length === 0,
  });

  const availableDomains = useMemo(() => {
    const tagsById = new Map<string, Tag>();
    for (const row of domainData?.organization_tag || []) {
      const tag = row.tag;
      if (tag?.tag_id) {
        tagsById.set(tag.tag_id, tag as Tag);
      }
    }
    return Array.from(tagsById.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [domainData]);

  const [addPost] = useAddPostMutation();
  const [addPostTags] = useAddPostTagsMutation();

  const handleSubmit = async () => {
    if (!orgId || !user?.user_id) {
      setError("Select an organization before posting.");
      return;
    }
    if (!title.trim() || !body.trim()) {
      setError("Title and body are required.");
      return;
    }
    if (!audienceRole) {
      setError("Select an audience role.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const result = await addPost({
        variables: {
          object: {
            org_id: orgId,
            author_id: user.user_id,
            title: title.trim(),
            body: body.trim(),
            audience_role: audienceRole,
            parent_post_id: null,
          },
        },
      });
      const postId = result.data?.insert_post_one?.post_id;
      if (!postId) {
        throw new Error("Failed to create post");
      }

      if (selectedDomains.length > 0) {
        await addPostTags({
          variables: {
            objects: selectedDomains
              .filter((tag) => tag.tag_id)
              .map((tag) => ({
                post_id: postId,
                tag_id: tag.tag_id,
              })),
          },
        });
      }

      try {
        await API.post(API_ROUTES.POSTS.NOTIFY(postId));
      } catch (notifyError) {
        console.error("Failed to notify org members:", notifyError);
      }

      await awardVetcoins({
        userId: user.user_id,
        amount: VETCOIN_REWARDS.POST_CREATED,
        actionType: "POST_CREATED",
        sourcePostId: postId,
      });

      navigate(`/posts/${postId}`);
    } catch (e: any) {
      setError(e?.message || "Could not create post");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayoutContainer title="New Vet Discussion">
      {!orgId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select an organization to create a post.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack spacing={2} maxWidth={720}>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
        />
        <TextField
          label="Body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
          required
          multiline
          minRows={6}
        />
        <FormControl fullWidth required>
          <InputLabel id="post-audience-role-label">Audience</InputLabel>
          <Select
            labelId="post-audience-role-label"
            label="Audience"
            value={audienceRole}
            onChange={(e) =>
              setAudienceRole(e.target.value as PostAudienceRole)
            }
          >
            {AUDIENCE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" mt={0.5}>
            All roles see the post when Audience is All. Otherwise only the
            selected role can see it. Admins always see every post.
          </Typography>
        </FormControl>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={0.5}
          >
            Domains
          </Typography>
          <TagManager
            tags={
              selectedDomains.length > 0
                ? selectedDomains
                : [MY_ORG_ONLY_DOMAIN]
            }
            availableTags={availableDomains}
            tagType={TAG_TYPE_DOMAIN}
            placeholder="Select Domain"
            isEditing={myOrgIds.length > 0}
            onAdd={(tag) =>
              setSelectedDomains((prev) =>
                prev.some((t) => t.tag_id === tag.tag_id)
                  ? prev
                  : [...prev, tag],
              )
            }
            onDelete={(tag) => {
              if (!tag.tag_id) return; // "My Org only" default is not removable
              setSelectedDomains((prev) =>
                prev.filter((t) => t.tag_id !== tag.tag_id),
              );
            }}
          />
          <Typography variant="caption" color="text.secondary">
            By default the post stays in your organization. Select a domain to
            share it with every organization in that domain.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !orgId}
          >
            {submitting ? "Posting..." : "Publish"}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(AdminRoutes.Posts)}
            disabled={submitting}
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </MainLayoutContainer>
  );
};
